/**
 * @file 平台能力接口定义
 * @description 定义多端平台抽象层的核心类型与接口
 */

import type { useUserStore } from '@h5-journey/stores'

/** 平台标识枚举 */
export enum PlatformType {
  /** Flutter APP WebView */
  APP = 'app',
  /** 纯 H5 移动端浏览器 */
  H5 = 'h5',
  /** PC 浏览器 */
  PC = 'pc',
  /** 微信小程序 WebView */
  MINIPROGRAM = 'miniprogram'
}

/** App 环境专属配置 */
export interface AppOptions {
  /** 跳转类型，默认 'push' */
  type?: 'push' | 'go' | 'replace'
  /** 是否展示关闭按钮 */
  showClose?: boolean
}

/** 导航选项 */
export interface NavigateToOptions {
  /** 页面标题（APP 环境使用） */
  title?: string
  /** 是否保留历史记录，默认 true */
  hasHistory?: boolean
  /** 跳转成功后的回调函数 */
  cb?: () => void
  /** App 环境专属配置，仅 App 生效 */
  appOptions?: AppOptions
}

/** 登录选项 */
export interface LoginOptions {
  /** 登录成功后返回的 URL */
  returnUrl?: string
  /** 登录成功回调（APP 环境生效） */
  onSuccess?: () => void
  /** 是否保留历史记录（仅微信小程序环境生效，决定 navigateTo 还是 redirectTo），默认 true */
  hasHistory?: boolean
  /** 自定义登录页路径（H5/PC 环境生效），默认 '/login' */
  loginPath?: string
  /** 透传给小程序原生登录页的额外参数（仅微信小程序环境生效） */
  extraParams?: Record<string, string>
}

/** 分享数据 */
export interface ShareData {
  title?: string
  desc?: string
  link?: string
  imgUrl?: string
  [key: string]: unknown
}

// 定位返回结构化结果
export interface LocationResult {
  /** 城市展示名（业务直接使用） */
  city: string
  /** 城市编码 */
  cityId?: string
  /** 经度 */
  longitude?: number
  /** 纬度 */
  latitude?: number
  /** 异常描述信息 */
  message?: string
}

/** 地图导航选项 */
export interface LocationNavigateOptions {
  /** 经度 格式：114492511 */
  longitude: number
  /** 纬度 格式：30500335*/
  latitude: number
  /** 名称 */
  name?: string
  /** 地图类型 目前只对H5生效 */
  type?: 'amap'
  /** 坐标系 默认：wgs84 目前只对H5生效 */
  coordinate?: 'wgs84' | 'gcj02' | 'gaode'
}

/** 导航能力接口 */
export interface INavigationStrategy {
  /** 打开新页面 */
  navigateTo(url: string, options?: NavigateToOptions): void
  /** 返回上一页 */
  goBack(): void
  /** 回到首页 */
  goHome(): void
  /** 关闭当前页 */
  closePage(): void
  /** 跳转登录 */
  toLogin(options?: LoginOptions): Promise<void>
}

/** 通信能力接口 */
export interface ICommunicationStrategy {
  /** 拨打电话 */
  makePhoneCall(phoneNumber: string): void
  /** 分享 */
  share(data: ShareData): Promise<void>
}

/** 定位能力接口 */
export interface ILocationStrategy {
  /** 获取完整结构化定位结果 */
  getLocation(): Promise<LocationResult>
  /** 地图导航 */
  locationNavigate(options: LocationNavigateOptions): void
}

/** Bootstrap 配置选项（从 bootstrap 包传入） */
export interface BootstrapOptions {
  router?: unknown
  defaultTerminalCode?: string
  defaultChannelId?: string
  defaultSourceFlag?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onLoginSuccessModal?: (data: any) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

/** 平台初始化上下文 */
export interface PlatformInitContext {
  userStore: ReturnType<typeof useUserStore>
  options: BootstrapOptions
}

/** 完整的平台策略接口 */
export interface IPlatformStrategy {
  /** 平台标识 */
  type: PlatformType
  /** 平台初始化钩子（在 bootstrap 中调用） */
  onInit(context: PlatformInitContext): Promise<void>
  /** 导航能力 */
  navigation: INavigationStrategy
  /** 通信能力 */
  communication: ICommunicationStrategy
  /** 定位能力 */
  location: ILocationStrategy
}

/** 单个策略覆盖项 */
export interface StrategyOverride {
  /**
   * 生效的平台条件
   * - 不传：所有平台都生效
   * - 单值：仅该平台生效
   * - 数组：数组中任一平台匹配即生效
   */
  platform?: PlatformType | PlatformType[]

  /** 导航能力覆盖（只需提供要覆盖的方法，其余保持原策略实现） */
  navigation?: Partial<INavigationStrategy>

  /** 通信能力覆盖 */
  communication?: Partial<ICommunicationStrategy>

  /** 定位能力覆盖 */
  location?: Partial<ILocationStrategy>
}
